import { useEffect, useRef, useState, useCallback } from "react";
import * as S from "./styles";
import { SVG } from "../SVG";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import useClickOutside from "~/hooks/useClickOutside";
import { categoryTranslations } from "~/pages/home/categories";
import { debounce } from "lodash";

export interface ICategories {
  id: number | string;
  name: string;
  is_visible: boolean;
  image: string;
  external_id: string;
}

type Props = {
  rows: number;
  columns: number;
  topLevelCategories: ICategories[];
  dropdownCategories?: ICategories[];
  fillRow?: string;
  onClick: (category: ICategories) => void;
};

const EXTRA_WIDTH = 100;

const Categories = ({
  topLevelCategories = [],
  dropdownCategories = [],
  rows,
  columns,
  fillRow,
  onClick,
}: Props) => {
  const { t } = useTranslation();
  const [dropdownCoords, setDropdownCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [dropdownSide, setDropdownSide] = useState<null | "left" | "right">(
    null
  );
  const isAboveLaptopL = useMediaQuery("laptopL");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const calculateDropdownSide = useCallback(() => {
    if (!dropdownContainerRef.current) return null;
    if (!isAboveLaptopL) return "right";
    const { x: containerX } =
      dropdownContainerRef.current.getBoundingClientRect();
    return containerX < window.innerWidth / 2 ? "left" : "right";
  }, [isAboveLaptopL]);

  const calculateDropdownCoords = useCallback(() => {
    if (!dropdownContainerRef.current) return null;
    const rect = dropdownContainerRef.current.getBoundingClientRect();
    const desiredWidth = rect.width + EXTRA_WIDTH;
    const newLeft = rect.left - (desiredWidth - rect.width);
    return { top: rect.bottom, left: newLeft, width: desiredWidth };
  }, []);

  useClickOutside(dropdownContainerRef, () => {
    setDropdownSide(null);
    setDropdownCoords(null);
  });

  useEffect(() => {
    if (dropdownSide === null || isAboveLaptopL) return;

    const updateCoords = () => {
      setDropdownCoords(calculateDropdownCoords());
    };

    const debouncedUpdateCoords = debounce(updateCoords, 50);

    window.addEventListener("resize", debouncedUpdateCoords);
    window.addEventListener("scroll", debouncedUpdateCoords, true);
    debouncedUpdateCoords();

    return () => {
      debouncedUpdateCoords.cancel();
      window.removeEventListener("resize", debouncedUpdateCoords);
      window.removeEventListener("scroll", debouncedUpdateCoords, true);
    };
  }, [dropdownSide, isAboveLaptopL, calculateDropdownCoords]);

  useEffect(() => {
    if (dropdownSide === null) return;
    const grid = dropdownContainerRef.current?.parentElement;
    if (!grid) return;

    const handleScroll = () => {
      setDropdownSide(null);
      setDropdownCoords(null);
    };

    grid.addEventListener("scroll", handleScroll, { passive: true });
    return () => grid.removeEventListener("scroll", handleScroll);
  }, [dropdownSide]);

  useEffect(() => {
    const handleResize = () => {
      setDropdownSide(null);
      setDropdownCoords(null);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <S.CategoriesGrid rows={rows} columns={columns}>
      {topLevelCategories.map((category, index) => {
        if (!category.is_visible) return null;
        return (
          <S.CategoryItem
            data-testid="category-item"
            onClick={() => onClick(category)}
            key={index}
            fillRow={fillRow === category.name}>
            <S.CategoryImage
              src={category.image}
              alt={category.name}
              width={76}
              height={76}
              loading="lazy"
              decoding="async"
            />
            <S.Text>
              {t(
                categoryTranslations[
                  category.name as keyof typeof categoryTranslations
                ]
              )}
            </S.Text>
          </S.CategoryItem>
        );
      })}

      {dropdownCategories.length > 0 && (
        <S.DropdownContainer ref={dropdownContainerRef}>
          <S.DropdownButton
            onClick={() => {
              setDropdownSide((prev) => {
                if (prev !== null) {
                  setDropdownCoords(null);
                  return null;
                }
                if (!isAboveLaptopL) {
                  setDropdownCoords(calculateDropdownCoords());
                }
                return calculateDropdownSide();
              });
            }}>
            {t("dropshipping.all_categories")}
            <SVG icon={faAngleDown} />
          </S.DropdownButton>

          {dropdownSide !== null && (
            <S.DropdownContent
              side={dropdownSide}
              dropdownTop={dropdownCoords?.top}
              dropdownLeft={dropdownCoords?.left}
              dropdownWidth={dropdownCoords?.width}>
              {dropdownCategories.map((category) => (
                <S.DropdownItem
                  key={category.id}
                  onClick={() => {
                    onClick(category);
                    setDropdownSide(null);
                  }}>
                  {t(
                    categoryTranslations[
                      category.name as keyof typeof categoryTranslations
                    ]
                  )}
                </S.DropdownItem>
              ))}
            </S.DropdownContent>
          )}
        </S.DropdownContainer>
      )}
    </S.CategoriesGrid>
  );
};

export default Categories;
